import { Injectable } from '@angular/core';
import { ReportsDownloadService } from './reports-download.service';
import { QueryStateService } from './query-state.service';
import { SearchOutputModel } from '../models/search-output-model';
import { PubSearchService } from './pub-search.service';
import { AdamsDocModel } from '../models/adams-doc-model';
import { BehaviorSubject } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ResultSelectionService } from './result-selection.service';
import { environment } from '../../environments/environment';
import { FoldersService } from './folders.service';
import { SearchFilter } from '../models/search-filter-model';
import { ConstantsService } from './constants.service';

@Injectable({
  providedIn: 'root'
})
export class ReportsService {

  // downloadFileType: string = 'Excel';
  isExcelDownload = true;
  downloadOption: string = 'all';
  selectedProps: any[] = [];

  private totalCountSubject = new BehaviorSubject<number>(0);
  public totalCount$ = this.totalCountSubject.asObservable();

  private downloadFileTypeSubject = new BehaviorSubject<string>('Excel');
  public downloadFileType$ = this.downloadFileTypeSubject.asObservable();

  private isDownloadingSubject = new BehaviorSubject<boolean>(false);
  public isDownloading$ = this.isDownloadingSubject.asObservable();

  private cancelDownloadSubject = new BehaviorSubject<boolean>(false);
  public cancelDownload$ = this.cancelDownloadSubject.asObservable();

  private progressSubject = new BehaviorSubject<number>(0);
  public progress$ = this.progressSubject.asObservable();

  constructor(private downloadService: ReportsDownloadService,
    private queryStateSvc: QueryStateService,
    private searchSvc: PubSearchService,
    private selectionSvc: ResultSelectionService,
    private folderSvc: FoldersService,
    private msgSvc: MessageService,
    private constants: ConstantsService) { }

  setIsDownload(data: boolean) {
    this.isDownloadingSubject.next(data);
  }

  getIsDownload(): boolean {
    return this.isDownloadingSubject.value;
  }

  setCancelDownload(data: boolean) {
    this.cancelDownloadSubject.next(data);
  }

  getCancelDownload(): boolean {
    return this.cancelDownloadSubject.value;
  }

  setProgress(data: number) {
    this.progressSubject.next(data);
  }

  getProgress(): number {
    return this.progressSubject.value;
  }

  setTotalCount(count: number) {
    this.totalCountSubject.next(count);
  }

  getTotalCount(): number {
    return this.totalCountSubject.value;
  }

  setFileType(fileType: string) {
    this.downloadFileTypeSubject.next(fileType);
  }

  getFileType(): string {
    return this.downloadFileTypeSubject.value;
  }

  startDownload() {
    this.setCancelDownload(false);
    this.setIsDownload(true);
    this.downloadService.clearItems();

    if (this.selectionSvc.isAllSelected() || this.selectionSvc.isAllExcept()) {
      this.ensureAllData();
    }
    else {
      this.downloadService.downloadedItems = this.selectionSvc.getSelected();
      this.beginDownload();
    }
    // this.ensureAllData();
  }

  ensureAllData() {
    let downloadItemCount = this.downloadService.downloadedItems.length;
    let tc = this.getTotalCount();

    if (tc > environment.results_max) {
      tc = environment.results_max;
    }

    if (tc > 0) {
      this.setProgress(Math.round(((downloadItemCount / tc) * 100)));
    }

    if (!this.getCancelDownload()) {
      if (downloadItemCount < tc) {
        this.setIsDownload(true);

        let sr = this.queryStateSvc.getUpdatedSearchRequest();
        sr.size = 500;
        sr.skip = downloadItemCount;

        sr.indexId = this.folderSvc.getSelectedLibrary();

        //Update Search Request to get the data for the selected folder.
        let selectedFolder = this.folderSvc.getSelectedFolder();

        if (selectedFolder.trim() !== '') {
          selectedFolder = this.folderSvc.ensureRootFolderSlash(selectedFolder);
          let fldrFilter: SearchFilter = {
            field: 'FoldersFiledIn',
            operator: this.constants.text_operator_equals,
            value: selectedFolder
          };
          sr.filters?.push(fldrFilter);
        }

        // console.log("query: ", JSON.stringify(sr));
        //get the next batch of data
        this.searchSvc.downloadItems(sr).subscribe((data: SearchOutputModel) => {
          if (data.results) {
            this.downloadService.downloadedItems = this.downloadService.downloadedItems.concat(data.results);
            this.filterExceptions();
          }

          this.ensureAllData();
        });
      }
      else {
        this.beginDownload();
      }
    }
    else {
      this.setIsDownload(false);
      this.msgSvc.add({ key: 'csvcancel', severity: 'error', summary: 'CSV Download Cancelled!' });
    }
  }

  beginDownload() {
    this.setIsDownload(false);
    if (this.downloadOption == 'selected') {
      this.downloadCustom();
    } else {
      this.downloadAll();
    }
  }

  filterExceptions() {
    let ex = this.selectionSvc.getExcluded();
    this.downloadService.downloadedItems = this.downloadService.downloadedItems.filter(item => !ex.some(e => e.document.AccessionNumber == item.document.AccessionNumber));
  }

  downloadAll() {
    //TODO: Match up the Reportable properties to this "All List"
    let headers: any[] = [
      'AccessionNumber',
      'DocumentTitle',
      'Url',
      'AuthorName',
      'AuthorAffiliation',
      'AddresseeName',
      'AddresseeAffiliation',
      'CaseReferenceNumber',
      'Comment',
      'ContactPerson',
      'Keyword',
      'DateAdded',
      'DateDocketed',
      'DocumentType',
      'DocumentDate',
      'DocumentReportNumber',
      'DocketNumber',
      'DistributionListCodes',
      'LicenseNumber',
      'PackageNumber',
      'MicroformAddresses',
      'ASLBPNumber',
      'Availability',
      'CUIDocumentSensitivity',
      'DatetobeReleased',
      'DocumentDateReceived',
      'DocumentStatus',
      'DocumentSensitivity',
      'ExhibitStatus',
      'FACADocument',
      'FoldersFiledIn',
      'HearingOfficialExhibitNumber',
      'HearingPartyExhibitNumber',
      'HearingPartyIdentifier',
      'OfficialRecord',
      'PublishedtoEHDDate',
      'RelatedDate',
      'Replicated',
      'TextSourceFlag',
      'VitalRecordsCategory',
      'PackagesFiledIn',
      'DateAddedTimestamp'
    ];

    this.executeDownload(headers);
  }

  getCustomHeaderNames(): string[] {
    return this.selectedProps.map(item => item['cogSearchKey']);
  }

  downloadCustom() {
    let tc = this.getCustomHeaderNames();
    if (typeof (tc) == 'string') {
      tc = JSON.parse(tc);
    }

    tc.unshift("Url");
    tc.unshift("DocumentTitle");
    tc.unshift("AccessionNumber");

    let headers: any[] = tc;

    this.executeDownload(headers);
  }

  executeDownload(headers: any[]) {
    let docs: AdamsDocModel[] = [];
    this.downloadService.downloadedItems.forEach(d => { docs.push(d.document); });

    const ft = this.getFileType();

    if (ft === 'CSV') {
      this.downloadService.csvDownload(headers, docs);
    }
    else {
      this.downloadService.exportAsExcelFile(headers, docs);
    }
  }
}
